"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Loader, CircleCheck } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function InquiryForm() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.15
  });
  return <section id="inquiry-form" className="dark bg-background text-foreground py-24 px-4 relative overflow-hidden">
      {}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
      backgroundImage: `repeating-linear-gradient(0deg, hsl(215 70% 22%) 0px, transparent 1px, transparent 40px),
                           repeating-linear-gradient(90deg, hsl(215 70% 22%) 0px, transparent 1px, transparent 40px)`
    }} />

      <div ref={ref} className={`max-w-2xl mx-auto relative transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}>
        <FormProvider className="w-full" formId="inquiry_form_contact" sectionName="inquiry-form" defaultValues={{
        full_name: "",
        company_name: "",
        email: "",
        phone: "",
        inquiry_type: "",
        message: ""
      }} validationRules={{
        full_name: {
          required: true,
          minLength: 2,
          maxLength: 100
        },
        company_name: {
          required: true,
          minLength: 2,
          maxLength: 150
        },
        email: {
          required: true,
          pattern: "email"
        },
        phone: {
          pattern: "phone"
        },
        inquiry_type: {
          required: true
        },
        message: {
          required: true,
          minLength: 10,
          maxLength: 1000
        }
      }} submitText="Submit Inquiry" successMessage="Thank you for your inquiry! Our team will review your request and respond within 24 hours." errorMessage="Unable to submit your inquiry. Please check your information and try again.">
          <InquiryFormContent />
        </FormProvider>
      </div>
    </section>;
}
function InquiryFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <Card className="bg-white dark:bg-primary-dark border border-border-strong/30 shadow-medium">
        <CardContent className="py-16 text-center">
          <div className="inline-flex items-center justify-center size-16 rounded-full bg-accent/10 mb-6">
            <CircleCheck className="size-8 text-accent" />
          </div>
          <h2 className="font-display text-2xl font-bold text-foreground mb-3">
            Inquiry Received
          </h2>
          <p className="text-muted-foreground mb-8 max-w-md mx-auto leading-relaxed">
            {successMessage}
          </p>
          <Button onClick={resetForm} variant="outline" className="btn-primary">
            Submit Another Inquiry
          </Button>
        </CardContent>
      </Card>;
  }
  return <Card className="bg-white dark:bg-primary-dark border border-border-strong/30 shadow-medium">
      <CardHeader className="border-b border-border">
        <CardTitle className="font-display text-3xl text-foreground">
          Inquiry Form
        </CardTitle>
        <CardDescription className="text-muted-foreground text-base mt-2">
          Complete the form below to reach our team. We respond to all inquiries within 24 hours.
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-8">
        <Form onSubmit={handleSubmit}>
          <div className="space-y-6">
            {}
            <FormField control={control} name="full_name" rules={{
            required: "Full name is required",
            minLength: {
              value: 2,
              message: "Minimum 2 characters"
            },
            maxLength: {
              value: 100,
              message: "Maximum 100 characters"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-foreground font-semibold">Full Name</FormLabel>
                  <FormControl>
                    <Input placeholder="John Doe" className="transition-all duration-[var(--duration-fast)] focus:border-accent" {...field} />
                  </FormControl>
                  <FormDescription className="text-sm text-muted-foreground">
                    Enter your name as you would like us to address you.
                  </FormDescription>
                  <FormMessage />
                </FormItem>} />

            {}
            <FormField control={control} name="company_name" rules={{
            required: "Company name is required",
            minLength: {
              value: 2,
              message: "Minimum 2 characters"
            },
            maxLength: {
              value: 150,
              message: "Maximum 150 characters"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-foreground font-semibold">Company / Organization</FormLabel>
                  <FormControl>
                    <Input placeholder="Acme Trading Ltd." className="transition-all duration-[var(--duration-fast)] focus:border-accent" {...field} />
                  </FormControl>
                  <FormDescription className="text-sm text-muted-foreground">
                    The name of the business you represent.
                  </FormDescription>
                  <FormMessage />
                </FormItem>} />

            {}
            <FormField control={control} name="email" rules={{
            required: "Email is required",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Invalid email format"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-foreground font-semibold">Email Address</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="john@example.com" className="transition-all duration-[var(--duration-fast)] focus:border-accent" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            {}
            <FormField control={control} name="phone" rules={{
            pattern: {
              value: /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/,
              message: "Invalid phone number"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-foreground font-semibold">Phone Number</FormLabel>
                  <FormControl>
                    <Input type="tel" placeholder="+971 55 123 4567" className="transition-all duration-[var(--duration-fast)] focus:border-accent" {...field} />
                  </FormControl>
                  <FormDescription className="text-sm text-muted-foreground">
                    Optional — include country code for faster response.
                  </FormDescription>
                  <FormMessage />
                </FormItem>} />

            {}
            <FormField control={control} name="inquiry_type" rules={{
            required: "Please select an inquiry type"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-foreground font-semibold">Inquiry Type</FormLabel>
                  <FormControl>
                    <select {...field} className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-xs transition-all duration-[var(--duration-fast)] outline-none focus-visible:border-accent focus-visible:ring-accent/50 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm dark:bg-input/30">
                      <option value="" disabled>Select an option</option>
                      <option value="Product Availability">Product Availability</option>
                      <option value="Pricing & Quote">Pricing &amp; Quote</option>
                      <option value="Logistics & Shipping">Logistics &amp; Shipping</option>
                      <option value="Quality & Compliance">Quality &amp; Compliance</option>
                      <option value="Partnership Opportunities">Partnership Opportunities</option>
                      <option value="Other">Other</option>
                    </select>
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            {}
            <FormField control={control} name="message" rules={{
            required: "Message is required",
            minLength: {
              value: 10,
              message: "Minimum 10 characters"
            },
            maxLength: {
              value: 1000,
              message: "Maximum 1000 characters"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-foreground font-semibold">Message</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Please provide details of your inquiry..." rows={6} className="resize-none transition-all duration-[var(--duration-fast)] focus:border-accent" {...field} />
                  </FormControl>
                  <FormDescription className="text-sm text-muted-foreground">
                    The more information you give, the better we can assist you.
                  </FormDescription>
                  <FormMessage />
                </FormItem>} />

            {}
            {submitError && <div className="p-4 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-md">
                <p className="text-red-800 dark:text-red-400 text-sm">{submitError}</p>
              </div>}

            {}
            <Button type="submit" disabled={isSubmitting} className="w-full btn-primary h-12 text-base font-display font-semibold shadow-medium hover:shadow-strong hover:-translate-y-0.5 transition-all duration-[var(--duration-fast)]">
              {isSubmitting ? <>
                  <Loader className="animate-spin size-5" />
                  <span>Submitting...</span>
                </> : "Submit Inquiry"}
            </Button>
          </div>
        </Form>
      </CardContent>
    </Card>;
}